import removeNull from '@/api/lib/helpers/removeNull';
import removeEmptyStrings from '@/api/lib/helpers/removeEmptyStrings';
import removeEmptyObjects from '@/api/lib/helpers/removeEmptyObjects';

const diff = (lo, ro, options = {}) => {
    options = {
        retainArrayValues: false,
        retainObjectValues: false,
        removeNull: false,
        removeEmptyStrings: false,
        removeEmptyObjects: false,
        ignoreKeys: [],
        ...options
    };

    // The diffHelper function is a recursive helper that will be used to compare the input objects.
    const diffHelper = (lo, ro, retainObjectValues = false) => {
        // If both values are equal, return undefined as there is no difference.
        if (lo === ro) {
            return undefined;
        }

        // If both values are arrays, compare each item in the arrays.
        if (Array.isArray(lo) && Array.isArray(ro)) {
            const result = [];
            let arrayChanged = false;
            for (let i = 0; i < Math.max(lo.length, ro.length); i++) {
                const itemDiff = diffHelper(lo[`${i}`], ro[`${i}`], options.retainObjectValues);

                // If there is a difference between the two items, record it.
                if (itemDiff !== undefined) {
                    arrayChanged = true;
                    // If retainArrayValues is true and both lo[i] and itemDiff are objects,
                    // merge the objects before pushing to the result array.
                    if (options.retainArrayValues && typeof lo[`${i}`] === 'object' && typeof itemDiff === 'object') {
                        result.push({ ...lo[`${i}`], ...itemDiff });
                    } else {
                        result.push(itemDiff);
                    }
                } else if (options.retainArrayValues) {
                    // If retainArrayValues is true, add the unchanged item to the result array.
                    result.push(lo[`${i}`]);
                }
            }

            // If the array has changed, return the result array. Otherwise, return undefined.
            return arrayChanged ? result : undefined;
        }

        // If both values are objects (but not arrays), compare their properties.
        if (lo && ro && typeof lo === 'object' && typeof ro === 'object') {
            const keys = new Set([...Object.keys(lo), ...Object.keys(ro)]);

            const result = {};
            let objectChanged = false;

            // Iterate through all the unique keys in both objects.
            for (const key of keys) {
                if (options.ignoreKeys.includes(key)) {
                    continue;
                }
                const itemDiff = diffHelper(lo[`${key}`], ro[`${key}`], options.retainObjectValues);
                // If there is a difference for the current key, record it in the result object.
                if (itemDiff !== undefined) {
                    objectChanged = true;
                    result[`${key}`] = itemDiff;
                }
            }

            if (objectChanged && retainObjectValues) {
                // If objectChanged and retainObjectValues are true, include all other values from the same object.
                return { ...lo, ...ro, ...result };
            } else if (objectChanged && 'currency' in lo && 'amount' in lo) {
                // Currency objects should be treated as individual
                return { ...lo, ...result };
            } else if (objectChanged && 'region' in lo && 'national_number' in lo) {
                // Phone number objects should be treated as individual
                return { ...lo, ...result };
            }

            // If the object has changed, return the result object. Otherwise, return undefined.
            return objectChanged ? result : undefined;
        }

        // If the values are not equal, return the right-side value as the difference.
        if (lo !== ro) {
            return ro;
        }

        return undefined;
    };

    if (options.removeNull) {
        lo = removeNull(lo);
        ro = removeNull(ro);
    }

    if (options.removeEmptyStrings) {
        lo = removeEmptyStrings(lo);
        ro = removeEmptyStrings(ro);
    }

    if (options.removeEmptyObjects) {
        lo = removeEmptyObjects(lo);
        ro = removeEmptyObjects(ro);
    }

    // Call the diffHelper function with the input objects and return the result.
    // If the result is undefined, return an empty object.
    let output = diffHelper(lo, ro);

    return output ? output : {};
};

export default diff;
